// HQ's payments oversight for one organisation: its payee at the payment provider and its commission (F-PAY-05,
// F-PAY-06, F-HQ-13; spec/14 §5, spec/06 §9.4–§9.5, ADR-0061).
//
//   GET  /v1/hq/organizations/{organizationId}/payments                  the setup: provider, commission, payee   hq:payments
//   PUT  /v1/hq/organizations/{organizationId}/payments/payee            onboard or correct its payee             hq:payments
//   POST /v1/hq/organizations/{organizationId}/payments/payee/refresh    ask the provider for the payee's status  hq:payments
//
// **The merchant model** (the owner, 2026-10-12): the Operator has one account with the payment provider
// (Cashfree). Each café — the organisation — is onboarded by HQ as a **payee** (a Cashfree Easy Split vendor) with
// its bank account and KYC, and needs no account of its own. Every online payment is captured into the Operator's
// account and **split**: the café's share to its payee, the Operator's commission kept, the provider's fee taken
// from the café's share. Online payments open at a venue only when the payee is `active`, `payments.onlineEnabled`
// is on and a provider is configured (spec/06 §9.5).
//
// **What is stored and shown.** The full bank account number and PAN go to the provider and are never stored:
// the payee shows the last four digits of each, the IFSC, the account holder, the GSTIN (public) and the contact.
// A correction is sent whole (the provider re-checks the bank account), never a patch of a masked value.
//
// **The commission** is the organisation setting `payments.commissionBps` (0 by default, the founding offer). It is
// read here; HQ changes it on the organisation's settings (`PUT /v1/hq/organizations/{o}/settings`, `hq:settings`),
// which the organisation sees but cannot change.
//
// Platform scope: every route needs a platform role holding `hq:payments` and a second factor on the session. Each
// change writes one audit row in the organisation's log, masked.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { HQ_PAYMENTS } from "../permissions/index.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/**
 * Where the provider is with a payee (spec/06 §9.5): `pending` (KYC and the bank check running),
 * `action_required` (the provider needs more from the business), `active` (the only status that opens online
 * payments), `rejected`, `suspended`.
 */
export const PayeeStatusSchema = z
    .enum(['pending', 'action_required', 'active', 'rejected', 'suspended'])
    .openapi('PayeeStatus', { description: 'Where the payment provider is with an organisation’s payee (spec/06 §9.5).' });
/** The legal form of the business the payouts go to. */
export const PayeeBusinessTypeSchema = z
    .enum(['individual', 'proprietorship', 'partnership', 'private_limited', 'llp'])
    .openapi('PayeeBusinessType', { description: 'The legal form of the business a payee is.' });
/** The organisation's payee as HQ and the platform see it: masked. */
export const PayeeSchema = z
    .object({
    /** The payment provider it is held by (`cashfree`). */
    provider: z.string().min(1).max(40),
    /** The provider's id for it (the platform's own, from the organisation's id). */
    providerAccountId: z.string().min(1).max(64),
    status: PayeeStatusSchema,
    businessName: z.string().min(1).max(200),
    businessType: PayeeBusinessTypeSchema,
    /** The last four characters of the PAN; the PAN itself is not stored. */
    panLast4: z.string().min(1).max(4),
    gstin: z.string().length(15).nullable(),
    contactName: z.string().min(1).max(200),
    contactEmail: z.string().min(3).max(320),
    contactPhone: z.string().min(10).max(16),
    bankAccountHolder: z.string().min(1).max(200),
    /** The last four digits of the bank account; the number itself is not stored. */
    bankAccountLast4: z.string().min(1).max(4),
    bankIfsc: z.string().length(11),
    /** When the provider's status was last read or reported. */
    statusCheckedAt: IsoDateTimeSchema.nullable(),
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Payee', { description: 'An organisation’s payee at the payment provider, masked (spec/06 §9.5, ADR-0061).' });
/** One organisation's online-payments setup, as HQ sees it. */
export const HqOrganizationPaymentsSchema = z
    .object({
    /** The deployment's payment provider (`cashfree`), or null when none is configured. */
    provider: z.string().min(1).max(40).nullable(),
    /**
     * `split`: the provider pays each organisation through its payee (online payments wait for it to be active).
     * `merchant`: everything settles to the platform (no payee). `none`: no provider is configured.
     */
    settlement: z.enum(['split', 'merchant', 'none']),
    /** `payments.commissionBps` for the organisation: the platform's commission, in basis points (100 = 1%). */
    commissionBps: z.int().min(0).max(10_000),
    /** The payee, once HQ has onboarded one. */
    payee: PayeeSchema.nullable(),
})
    .openapi('HqOrganizationPayments', { description: 'An organisation’s online-payments setup, as HQ sees it (spec/14 §5).' });
/**
 * Onboard or correct a payee: everything, every time — the provider re-checks the bank account on a correction.
 * The account number and the PAN are sent to the provider and not stored.
 */
export const SavePayeeSchema = z
    .object({
    businessName: z.string().trim().min(3).max(100),
    businessType: PayeeBusinessTypeSchema,
    /** Ten characters: five letters, four digits, a letter. */
    pan: z.string().trim().regex(/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/),
    /** Fifteen characters, issued to the same PAN; omitted when the business has none. */
    gstin: z.string().trim().regex(/^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z][1-9A-Za-z][Zz][0-9A-Za-z]$/).optional(),
    contactName: z.string().trim().min(2).max(100),
    contactEmail: z.email().max(100),
    /** A ten-digit Indian mobile number (with or without +91). */
    contactPhone: z.string().trim().min(10).max(16),
    bankAccountHolder: z.string().trim().min(3).max(100),
    /** 9 to 18 digits. */
    bankAccountNumber: z.string().trim().regex(/^[0-9]{9,18}$/),
    /** Eleven characters: four letters, a zero, six letters or digits. */
    bankIfsc: z.string().trim().regex(/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/),
})
    .strict()
    .openapi('SavePayeeRequest', { description: 'The details a payee is onboarded or corrected with (ADR-0061). Not stored whole.' });
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const forbidden = problemResponse('Not HQ, no `hq:payments`, or no second factor on the session.', 'forbidden', 'second-factor-required');
export const getHqOrganizationPaymentsRoute = createRoute({
    method: 'get',
    path: '/v1/hq/organizations/{organizationId}/payments',
    operationId: 'getHqOrganizationPayments',
    tags: ['hq'],
    summary: 'An organisation’s online-payments setup (F-PAY-05, F-HQ-13)',
    description: 'The configured payment provider, the organisation’s commission (`payments.commissionBps`) and its payee, masked. ' +
        'Read through the audited door (one row in the organisation’s log). Platform scope: requires `hq:payments` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_PAYMENTS,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(HqOrganizationPaymentsSchema, 'The setup.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const saveHqOrganizationPayeeRoute = createRoute({
    method: 'put',
    path: '/v1/hq/organizations/{organizationId}/payments/payee',
    operationId: 'saveHqOrganizationPayee',
    tags: ['hq'],
    summary: 'Onboard or correct an organisation’s payee (F-PAY-05)',
    description: 'Creates the organisation’s payee at the payment provider (a Cashfree Easy Split vendor) with its bank account and ' +
        'KYC, or corrects the one it has, then records the provider’s status. The full account number and PAN go to the ' +
        'provider only; the payee keeps the last four of each. A provider that does not split has no payees (`409`). A ' +
        'provider that refuses the details answers `400` with its reason; one that cannot be reached, `503`. Audited in the ' +
        'organisation’s log, masked. Platform scope: requires `hq:payments` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_PAYMENTS,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: SavePayeeSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(PayeeSchema, 'The payee, masked, with the provider’s status.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such organisation.', 'not-found'),
        409: problemResponse('No payment provider is configured, or it does not pay through payees.', 'online-payments-unavailable', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The payment provider could not be reached. Try again.', 'payment-provider-unavailable'),
    },
});
export const refreshHqOrganizationPayeeRoute = createRoute({
    method: 'post',
    path: '/v1/hq/organizations/{organizationId}/payments/payee/refresh',
    operationId: 'refreshHqOrganizationPayee',
    tags: ['hq'],
    summary: 'Ask the payment provider where it is with an organisation’s payee (F-PAY-05)',
    description: 'Reads the payee’s status from the provider and records it (the provider also reports changes by webhook). Audited. ' +
        'Platform scope: requires `hq:payments` and a second factor.',
    [PERMISSION_EXTENSION]: HQ_PAYMENTS,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(PayeeSchema, 'The payee with its status now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such organisation, or it has no payee yet.', 'not-found'),
        409: problemResponse('No payment provider is configured, or it does not pay through payees.', 'online-payments-unavailable', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
        503: problemResponse('The payment provider could not be reached. Try again.', 'payment-provider-unavailable'),
    },
});
//# sourceMappingURL=hq-payments.js.map